/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
        import { EzChar } from '/ezlibrary/helpers/EzChar.js';
        import { EzString } from '/ezlibrary/helpers/EzString.js';
        import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
        import { EzArray } from '/ezlibrary/helpers/EzArray.js';
        import { EzMap } from '/ezlibrary/helpers/EzMap.js';
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';


/**
 * @class
 * @description
 * Static class that provides utility methods and properties for Objects
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzFloat extends EzNumber {
    /**
     * @public @readonly @property
     * Gets floating point constant 0.00
     * @returns {number}
     */
    static get ZERO() {
        return 0.00;
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseFloat)
     * @param {string} stringValue
     * @returns {number}
     * The provided stringValue transformed to a float number if successful.
     * Returns 0.00 if the provided stringValue is undefined, null, or an empty string.
     * Returns 0.00 if the attempt to transform the provided stringValue fails.
     */
    static toFloat(stringValue) {
        if (!stringValue || 'string' != typeof stringValue || 0 == stringValue.length) {
            // fall to zero if the string value is null, empty, or undefined
            return EzFloat.ZERO;
        }

        try {
            const floatResult = parseFloat(stringValue);

            return !isNaN(floatResult)
                ? floatResult
                : EzFloat.ZERO;
        } catch (err) {
            if (EzObject.isValid(globalThis?.console)) {
                globalThis.console.error(
                    `Failed to transform string "${stringValue}" to a float number due to the following error: ${err.message}`);
            }

            return EzFloat.ZERO;
        }
    }
}
